<template>
  <div class="grid-order-documents">
    <div v-if="isLoadingMetadata" class="loading-container">
      <BackstageIcon :name="Icons.CIRCLE_LOADING" />
      <span>{{ $t('viewOrder.documents_grid.loading_documents') }}</span>
    </div>
    <template v-else>
      <div class="documents-group">
        <p class="title">
          {{ $t('viewOrder.documents_grid.confirmation_title') }}
        </p>

        <OrderDocumentItem
          v-for="document in confirmations"
          :key="document.id"
          :document="document"
          :user-locale="userLocale"
          @download="downloadDocument" />
      </div>

      <div v-if="showPackingSlips" class="documents-group">
        <p class="title">
          <label class="title">{{ $t('viewOrder.documents_grid.packing_slips_title') }}</label>
        </p>

        <OrderDocumentItem
          v-for="packingSlip in packingSlips"
          :key="packingSlip.id"
          :document="packingSlip"
          :user-locale="userLocale"
          @download="downloadDocument" />
      </div>

      <div v-if="invoices.length" class="documents-group">
        <p class="title">
          {{ $t('viewOrder.documents_grid.invoices_title') }}
        </p>

        <OrderDocumentItem
          v-for="invoice in invoices"
          :key="invoice.id"
          :document="invoice"
          :user-locale="userLocale"
          @download="downloadDocument" />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { useDocumentHelpers } from '@/composables/document-helpers';
import { useNotification } from '@/composables/useNotification';
import { ordersMixins } from '@/mixins/orders.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { Icons } from '@/models/icons.enum';
import { OrderDocument } from '@/models/order-document.model';
import { OrderDocumentService } from '@/services/order-document-service';
import { storeToRefs } from 'pinia';
import { defineComponent, reactive } from 'vue';
import { useLocale } from '../../composables/locale';
import { OrderDocumentType } from '../../models/order-document-type.enum';
import type { Order } from '../../models/order.model';
import { useUserStore } from '../../stores/user';
import BackstageIcon from '../common/BackstageIcon.vue';
import OrderDocumentItem from './order-document-item.vue';

export default defineComponent({
  name: 'grid-order-document',
  components: {
    OrderDocumentItem,
    BackstageIcon,
  },
  props: {
    order: {
      type: Object,
      default: null,
    },
  },
  setup() {
    const { openPDF } = useDocumentHelpers();

    return {
      openPDF,
      Icons,
    };
  },
  data() {
    const notify = useNotification();
    const { userLocale } = useLocale();
    const { selectedAccountId } = storeToRefs(useUserStore());

    return {
      orderDocumentService: new OrderDocumentService(),
      documents: new Array<OrderDocument>(),
      accountId: selectedAccountId,
      userLocale,
      notify,
      isLoadingMetadata: false,
    };
  },
  computed: {
    confirmations(): OrderDocument[] {
      return this.documents
        .filter(
          (document) =>
            document.orderDocumentType?.toLocaleLowerCase() === OrderDocumentType.OrderConfirmation.toLocaleLowerCase(),
        )
        .sort(this.documentSorter);
    },
    packingSlips(): OrderDocument[] {
      return this.documents
        .filter(
          (document) =>
            document.orderDocumentType?.toLocaleLowerCase() === OrderDocumentType.PackingSlip.toLocaleLowerCase(),
        )
        .filter((document) => document.isMissingFile !== true)
        .sort(this.documentSorter);
    },
    invoices(): OrderDocument[] {
      return this.documents
        .filter(
          (document) =>
            document.orderDocumentType?.toLocaleLowerCase() === OrderDocumentType.Invoice.toLocaleLowerCase(),
        )
        .sort(this.documentSorter);
    },
    showPackingSlips(): boolean {
      return this.packingSlips.length > 0;
    },
  },
  beforeMount() {
    this.documents = this.getDocuments();
    this.enrichAllDocumentMetaData();
  },
  methods: {
    ...ordersMixins,
    ...utilsMixins,
    async enrichAllDocumentMetaData() {
      this.isLoadingMetadata = true;
      try {
        const documentRequests = this.documents.map((document) => ({
          orderNumber: this.order.orderNumber,
          documentId: document.id,
          documentType: this.getDocumentType(document.orderDocumentType),
        }));

        const validation = await this.orderDocumentService.getMetaData(documentRequests, this.accountId);

        if (validation.isValid() && validation.model) {
          validation.model.forEach((documentModel) => {
            const document = this.documents.find(
              (doc) => doc.orderNumber === documentModel.orderNumber && doc.id === documentModel.documentId,
            );

            if (document) {
              document.isMissingFile = documentModel.isMissingFile;
            }
          });
        }
      } finally {
        this.isLoadingMetadata = false;
      }
    },
    async downloadDocument(document: OrderDocument) {
      if (document.loading) {
        return;
      }

      document.loading = true;

      const index = this.documents.indexOf(document);

      this.documents = reactive(this.documents);
      this.documents[index] = document;

      const validation = await this.orderDocumentService.get(
        this.order.orderNumber,
        document.id,
        this.accountId,
        this.getDocumentType(document.orderDocumentType),
      );

      if (validation.isValid() && validation.model?.base64Body) {
        this.openPDF(validation.model.base64Body, document.id, document.orderNumber);
      } else {
        this.notify.notifyWarning(this.$t('documentsCheckout.document_is_not_available'));
      }

      document.loading = false;
      this.documents[index] = document;
    },
    documentSorter(a: OrderDocument, b: OrderDocument): number {
      if (a.createdDate && b.createdDate) {
        return a.createdDate.localeCompare(b.createdDate);
      } else if (a.packingSlipDate && b.packingSlipDate) {
        return a.packingSlipDate.toString().localeCompare(b.packingSlipDate.toString());
      } else {
        return a.id.localeCompare(b.id);
      }
    },
    getDocuments(): OrderDocument[] {
      return this.getOrderHeaderDocuments(this.order as Order, this.$t, this.userLocale);
    },
  },
});
</script>

<style scoped lang="scss">
.grid-order-documents {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  &:hover {
    background: inherit;
  }

  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    width: 100%;

    span {
      font-size: 14px;
      color: var(--dark-gray-color);
    }
  }

  .columnsContainer {
    display: block;
    // The foloowing style can be used to split the row into two equal parts for double column documents
    //   display: flex;

    //   @include breakpoint(untilxlarge) {
    //     flex-direction: column;
    //   }

    //   .column {
    //     display: flex;
    //     flex-direction: column;
    //     align-items: flex-start;
    //     flex-basis: 50%;
    //   }
  }

  .documents-group {
    margin: 10px 0;

    &:first-child {
      margin-top: 20px;
    }

    &:last-child {
      margin-bottom: 0px;
    }

    .title {
      margin: 0 !important;
      padding-bottom: 6px;
    }
  }
}
</style>
